import Vue                  from "vue";
import { mapGetters }       from "vuex"
import { LoadingSpinner }   from "@/components";
import { 
    TmsService,
    ErrorLogService,
    PartsService  }         from "@/services/";
 
import { DnRouterLink }     from "@/components/Elements";
import { themeHelper }      from "@/components/mixins/themeHelper";
import { constants }        from "@/const" 
import { UserSettingsService, OemManualsService, OemManualsDiagramFormat } from "@/services";
import { FeatureFlagService } from "@/services/FeatureFlagService";
import { TourService } from "@/services/TourService";
import { UserService } from "@/services/UserService";
import { default as PartSearchTruckSelector } from "@/components/PartDiagramSearch/PartSearchTruckSelector/PartSearchTruckSelector.vue";
import PartSearchResultsPanel  from "@/components/PartDiagramSearch/PartSearchResultsPanel/PartSearchResultsPanel.vue";

/** Facet keys that come from the query string but should NOT appear in selectedFacets. */
const doNotAddSelectedFacets = ["OemDescription", "Model", "Page", "com"];

/** Query-string params injected by Global Login that are irrelevant to TMS. */
const ignoredQueryStringParams = ["auid", "cnt"];

/**
 * Selection-related query params managed by PartSearchTruckSelector.
 * When these are present, the child component will restore state and
 * emit "search-complete" — PartDiagramPage should NOT auto-search.
 */
const truckSelectionQueryParams = ["OemDescription", "Model", "Category", "ComponentGroup", "Component"];
const CMJ_BRAND_AUTH_BITMAP = 1 | 2 | 4;

/**
 * PartDiagramPage – Top-level page orchestrating the Part Diagram Search
 * experience. Owns the truck-selection sidebar (PartSearchTruckSelector) and
 * the results panel (PartSearchResultsPanel). Supports two search flows:
 *
 *  1. **Category / TMS flow** – traditional paginated list of parts fetched
 *     via TmsService.
 *  2. **Manuals flow** – diagram + parts cards fetched via
 *     OemManualsService, displayed side-by-side.
 */
export default Vue.extend({
    name: "PartDiagramPage",
    components: {
        LoadingSpinner,
        DnRouterLink,
        PartSearchTruckSelector,
        PartSearchResultsPanel
    },
    mixins: [
        themeHelper
    ],
    created() {
        // If query params exist (deep link / bookmark), auto-load results
        const queryKeys = Object.keys(this.$route.query).filter(
            (k) => !ignoredQueryStringParams.includes(k)
        );

        // If the query contains truck-selection params, the child
        // PartSearchTruckSelector will restore state and emit
        // "search-complete" — don't auto-search here.
        const hasSelectionParams = queryKeys.some((k) => truckSelectionQueryParams.includes(k));
        if (hasSelectionParams) return;

        if (queryKeys.length > 0) {
            const facets: Record<string, any> = {};
            queryKeys.forEach((prop) => {
                facets[prop] = this.$route.query[prop];
                if (!doNotAddSelectedFacets.includes(prop)) {
                    this.selectedFacets[prop] = this.$route.query[prop];
                }
            });
            if (this.IsNullOrUndefined(facets["Page"])) {
                facets["Page"] = 1;
            }
            // Move Part_Description into the search term and out of facets
            if (this.selectedFacets["Part_Description"]) {
                this.searchTerm = this.selectedFacets["Part_Description"];
                delete this.selectedFacets["Part_Description"];
            }
            this.queryFacets = facets;
            this.searchPerformed = true;
            this.performDiagramSearch();
        }
    },
    computed: {
        ...mapGetters([
            "user",
            "activeDealer",
            "audienceIds",
            "activeAccountId",
            "getContentItem",
            "partSearchDefaultSort",
            "collapseAllPartDescriptionsValue",
            "CurrentCategory",
            "partSearchDefaultSortValue"
        ]),
        /** Whether the current search results contain at least one part. */
        hasResults(): boolean {
            return (
                this.searchResults &&
                this.searchResults.Parts &&
                this.searchResults.Parts.length > 0
            );
        }
    },
    watch: {
        /**
         * Triggers the OEM Diagrams tour when the diagram SVG data is first loaded.
         * This occurs after the user selects a publication and navigates to the diagram results.
         */
        diagramSvgData(newValue: string) {
            if (newValue && !this.oemDiagramsTourLoaded) {
                this.$nextTick(() => {
                    this.loadOEMDiagramsTourIfEligible();
                });
            }
        }
    },
    mounted() {
        this.getUserSettings();
        this.showTourOEMDiagrams = FeatureFlagService.getFeatureEnabled(constants.FEATURE_NAMES.TOUR_OEM_DIAGRAMS);

        // Query params are managed via history.replaceState (bypassing Vue Router),
        // so $route.query does not update on browser back/forward. Listen to the
        // native popstate event to detect when the user navigates back to the
        // base URL and reset all search state accordingly.
        // Specific logic that the business requested instead of just changing the truck selection

        this._onPopState = () => {
            if (this.searchPerformed) {
                this.searchPerformed = false;
                this.searchResults = {};
                this.loadingSearchResults = false;
                this.isDiagramSearch = false;
                this.diagramUrl = "";
                this.diagramAltText = "";
                this.diagramSvgData = "";
                this.diagramLoading = false;
                this.diagramComponentName = "";
                this.showDiagram = false;
                this.queryFacets = {};
                this.selectedFacets = {};
                this.totalParts = 0;
                this.totalPartPages = 0;
                this.hasAltParts = false;
                this.searchTerm = "";
                this.currentSearchTerms = null;
                this.selectedSortTerm = "";
                this.oemDiagramsTourLoaded = false;
            }
        };
        window.addEventListener("popstate", this._onPopState);
    },
    beforeDestroy() {
        if (this._onPopState) {
            window.removeEventListener("popstate", this._onPopState);
        }
    },
    data: () => ({
        searchTerm: "",
        searchResults: {} as any,
        loadingSearchResults: false,
        searchPerformed: false,
        queryFacets: {} as any,
        selectedFacets: {} as any,
        hasAltParts: false,
        totalParts: 0,
        totalPartPages: 0,
        partsPerPage: 10,
        userpartSearchDefaultSort: "",
        sortByOptions: constants.SORT_BY_OPTIONS,
        selectedSortTerm: "",
        currentSearchTerms: null as any,
        diagramUrl: "",
        diagramAltText: "",
        diagramSvgData: "",
        /** True while the diagram for the newly selected component is being fetched. */
        diagramLoading: false,
        /** Name of the currently selected component, used as the diagram panel title. */
        diagramComponentName: "",
        showDiagram: false,
        isDiagramSearch: false,
        /** Tracks whether user settings are being fetched. */
        loadingUserSettings: false,
        userCollapsePartDescriptions: false,
        showTourOEMDiagrams: false,
        oemDiagramsTourLoaded: false,
    }),
    methods: {
        /** Loads user-specific TMS settings (sort preference, collapse descriptions). */
        async getUserSettings() {
            this.loadingUserSettings = true;
            try {
                const userSettings = this.$store.getters.userSettings;
                if (userSettings === undefined || userSettings.length === 0) {
                    const freshSettings = await UserSettingsService.GetUserSettings(this.user.UserId);
                    this.$store.commit("SET_USER_SETTINGS_TMS", freshSettings);
                }
                this.userCollapsePartDescriptions = this.collapseAllPartDescriptionsValue;
                this.userpartSearchDefaultSort = this.partSearchDefaultSort;
            } finally {
                this.loadingUserSettings = false;
            }
        },

        /** Loads and starts the OEM Diagrams tour if the user is eligible. */
        async loadOEMDiagramsTourIfEligible() {
            if (this.oemDiagramsTourLoaded || !this.showTourOEMDiagrams) {
                return;
            }

            let hasUserTourRecord = false;
            let isCMJDealer = false;
            let tourId = null;

            // Validate the user ID before proceeding
            const userId = Number(this.user?.UserId);
            if (!Number.isInteger(userId) || userId <= 0) {
                return;
            }

            try {
                const brandAuth = Number(this.activeDealer?.BrandAuth);
                isCMJDealer = Number.isInteger(brandAuth) && (brandAuth & CMJ_BRAND_AUTH_BITMAP) !== 0;

                // Fetch the tour information for the OEM Diagrams tour
                const toursResponse = await UserService.GetTour("OEM Diagrams Tour", constants.DealernetClientId, true);
                const tours = Array.isArray(toursResponse?.data) ? toursResponse.data : [];
                
                // Get the first tour and its ID if available
                const firstTour = tours[0];
                tourId = Number(firstTour?.TourId) || null;
                const tourIds = firstTour && firstTour.TourId ? [firstTour.TourId] : [];
                const tourNames = ["OEM Diagrams Tour"];
                
                // Fetch the user's tour record for the OEM Diagrams tour
                const userToursResponse = await UserService.GetUserTour(userId, constants.DealernetClientId, tourIds, tourNames);
                const userTours = Array.isArray(userToursResponse?.data) ? userToursResponse.data : [];

                // Check if user has completed or cancelled the tour
                hasUserTourRecord = userTours.some((tour) => {
                    if (!tour || !tour.EndDate) {
                        return false;
                    }
                    const parsedDate = new Date(tour.EndDate);
                    return !Number.isNaN(parsedDate.getTime());
                });
            } catch (error) {
                hasUserTourRecord = false;
            }

            this.oemDiagramsTourLoaded = true;

            if (this.user && this.user.UserId && this.showTourOEMDiagrams && isCMJDealer && !hasUserTourRecord) {
                TourService.startTour("OEM Diagrams Tour", { userId, tourId });
            }
        },

        /**
         * Builds a base search-input object (without facets) used by both
         * getCategoryParts and getDiagramParts.
         */
        /**
         * Builds a search-input object (without facets) suitable for TmsService.
         */
        GetSearchInputWithoutFacets(sortField: string, sortDirection: string, inStockFilter: boolean) {
            return {
                AccountId: this.activeAccountId,
                UserId: this.user.UserId,
                DealerCode: this.user.DealerCode,
                Audiences: this.audienceIds,
                SortField: sortField,
                SortDirection: sortDirection,
                InStockFilter: inStockFilter,
            };
        },

        /** Bubbles the hasAltParts event; only sets true once per search. */
        HasAltParts(emittedHasAlts: boolean) {
            if (!this.hasAltParts) {
                this.hasAltParts = emittedHasAlts;
            }
        },

        /** Resets all search state so the user can start a new truck selection. */
        redirectToTruckSelect() {
            this.searchPerformed = false;
            this.searchResults = {};
            this.loadingSearchResults = false;
            this.isDiagramSearch = false;
            this.diagramUrl = "";
            this.diagramAltText = "";
            this.queryFacets = {};
            this.selectedFacets = {};
            this.totalParts = 0;
            this.totalPartPages = 0;
        },

        /**
         * When the user selects a Serial_Range facet, automatically infer and
         * set the corresponding Serial_Prefix on the query.
         */
        setSerialPrefixOnSerialRangeSelect(query: Record<string, any>, prop: string) {
            const prefixKeys: string[] = this.searchResults.Facets.Serial_Prefix.map((kvp: any) => kvp.Key);
            if (!prefixKeys.length) return;

            const prefixLength = prefixKeys[0].length;
            const prefix = this.selectedFacets[prop].substring(0, prefixLength);
            
            // Only add prefix if the Serial_Range starts with a known prefix
            if (prefixKeys.includes(prefix)) {
                query["Serial_Prefix"] = prefix;
            }
        },

        /** Merges selected facets into queryFacets and triggers a new search. */
        UpdateFacets() {
            // OemManuals mode doesn't support facet filtering
            if (this.isDiagramSearch) {
                return;
            }
            
            this.$store.commit("SET_RECENT_COMPONENT", {
                name: `Tms_Facets_${this.searchResults.OemDescription}_${this.searchResults.Model}`,
                data: this.selectedFacets,
            });

            // Remove facet keys from queryFacets that the user has de-selected.
            // Preserve base search-context keys that are not user-selectable facets.
            const baseSearchKeys = ["OemDescription", "Model", "Category", "ComponentGroup", "Component", "Page"];
            Object.keys(this.queryFacets).forEach((key) => {
                if (!baseSearchKeys.includes(key) && !(key in this.selectedFacets)) {
                    delete this.queryFacets[key];
                }
            });

            Object.keys(this.selectedFacets).forEach((prop) => {
                if (prop === "Serial_Range") {
                    this.setSerialPrefixOnSerialRangeSelect(this.queryFacets, prop);
                }
                this.queryFacets[prop] = this.selectedFacets[prop];
            });
            this.queryFacets["Page"] = 1;
            this.performDiagramSearch();
        },

        /**
         * Core method that fetches parts via TmsService and enriches each with
         * FullPartDetails. Used by both the paginated category view and the
         * diagram view.
         *
         * @param sortTerm    - Sort field key
         * @param sortDirection - "Ascending" | "Descending"
         * @param pageNumber  - Page to fetch (null = fetch all)
         * @param inStockFilter - Whether to filter to in-stock parts only
         */
        async fetchTmsParts(sortTerm: string, sortDirection: string, pageNumber: number | null, inStockFilter: boolean) {
            // OemManuals mode uses its own loading path
            if (this.isDiagramSearch) {
                return;
            }
            
            this.loadingSearchResults = true;
            if (pageNumber != null) {
                this.queryFacets["Page"] = pageNumber;
            }

            const searchInput = this.GetSearchInputWithoutFacets(sortTerm, sortDirection, inStockFilter);
            searchInput["Facets"] = this.queryFacets;
            this.currentSearchTerms = searchInput;
            this.hasAltParts = false;

            // NOTE: The enrichment logic below (FullPartDetails per part) is
            // similar to loadOemManualsDiagram. Consider extracting a shared
            // enrichPartsWithFullDetails() helper if both paths diverge further.
            try {
                const searchResponse = await TmsService.GetPartSearchResults(searchInput);
                this.searchResults = searchResponse.data;
                this.totalParts = this.searchResults.TotalCount;
                this.totalPartPages = Math.ceil(this.totalParts / this.partsPerPage);
                const currentParts = this.searchResults.Parts;

                // Remove Category from facets so it doesn't show in the side menu
                // (already chosen during part selection)
                if (this.searchResults.Facets) {
                    delete this.searchResults.Facets["Category"];
                }

                // Enrich each part with FullPartDetails in parallel
                let countPartsLoaded = 0;
                await new Promise<void>((resolve) => {
                    if (!currentParts || currentParts.length === 0) {
                        resolve();
                        return;
                    }

                    currentParts.forEach((part: any, index: number) => {
                        const partCode = part.PartCode || part.OemPartCode;

                        PartsService.FullPartDetails(
                            this.activeAccountId,
                            this.user.UserId,
                            part.PartId,
                            this.audienceIds,
                            partCode
                        )
                            .then((fullPartDetail) => {
                                // Use $set so Vue's reactivity picks up the new property
                                this.$set(currentParts[index], "FullPartDetails", fullPartDetail.data);
                            })
                            .catch((err) => {
                                ErrorLogService.LogException(err);
                            })
                            .finally(() => {
                                // Backfill empty FullPartDetails with data from the search result
                                if (part.FullPartDetails && this.IsNullOrUndefined(part.FullPartDetails.OemPartCode)) {
                                    part.FullPartDetails.OemPartCode = part.OemPartCode;
                                    part.FullPartDetails.PartDescription = part.PopularDescription;
                                }
                                this.$set(currentParts[index], "OemInformation", part.OemInformation);

                                if (++countPartsLoaded === currentParts.length) {
                                    resolve();
                                }
                            });
                    });
                });

                // Guard against stale results (e.g. rapid pagination clicks)
                if (this.currentSearchTerms === searchInput) {
                    this.searchResults.Parts = currentParts;
                }
            } catch (ex) {
                ErrorLogService.LogException(ex);
            } finally {
                // Only clear loading if this is still the latest request
                if (this.currentSearchTerms === searchInput) {
                    this.loadingSearchResults = false;
                }
            }
        },

        /**
         * Fetches a page of category parts (paginated TMS flow).
         * Delegates to fetchTmsParts with a page number.
         */
        async getCategoryParts(sortTerm: string, sortDirection: string, pageNumber: number, inStockFilter: boolean) {
            await this.fetchTmsParts(sortTerm, sortDirection, pageNumber, inStockFilter);
        },

        /**
         * Fetches all diagram parts (non-paginated diagram flow).
         * Delegates to fetchTmsParts without pagination.
         */
        async getDiagramParts(sortTerm: string, sortDirection: string, pageNumber: number, inStockFilter: boolean) {
            await this.fetchTmsParts(sortTerm, sortDirection, pageNumber, false);
        },

        // ─── Combined page event handlers ──────────────────────────────

        /** Resets all search state when the user changes the make selection. */
        onMakeSelected(payload: { IsOemMake: boolean; brandId: any; showDiagram: boolean }) {
            this.searchResults = {};
            this.searchPerformed = false;
            this.queryFacets = {};
            this.selectedFacets = {};
            this.diagramUrl = "";
            this.diagramAltText = "";
            this.totalParts = 0;
            this.totalPartPages = 0;
            this.hasAltParts = false;
            this.isDiagramSearch = false;
            this.loadingSearchResults = false;

            // Hiding Diagram by default for now, switch back to this assignment when show diagram is apporved
            this.showDiagram = payload.showDiagram || false;
            this.showDiagram = false;
        },

        /** Clears search results when any selector input changes (model, component group, etc.). */
        onSelectionReset() {
            this.searchResults = {};
            this.searchPerformed = false;
            this.queryFacets = {};
            this.selectedFacets = {};
            this.diagramUrl = "";
            this.diagramAltText = "";
            this.diagramSvgData = "";
            this.diagramLoading = false;
            this.diagramComponentName = "";
            this.totalParts = 0;
            this.totalPartPages = 0;
            this.hasAltParts = false;
            this.isDiagramSearch = false;
            this.loadingSearchResults = false;
            this.oemDiagramsTourLoaded = false;
        },

        /**
         * Handles the search-complete event from PartSearchTruckSelector.
         * Routes to either the OemManuals diagram flow or the category list flow.
         */
        onSearchComplete(queryData: any) {
            // Strip keys that aren't part of the TMS facet query
            const nonFacetKeys = [
                "showDiagram", "userId", "brandId", "BrandID", "componentNumber",
                "componentGroupId", "componentGroup", "component", "truckType",
                "oemDescription", "model", "inlineParts", "diagramSvgData"
            ];
            const cleanFacets: Record<string, any> = {};
            Object.keys(queryData).forEach(key => {
                if (!nonFacetKeys.includes(key)) {
                    cleanFacets[key] = queryData[key];
                }
            });
            this.queryFacets = { ...cleanFacets, Page: 1 };
            this.selectedFacets = {};
            this.searchPerformed = true;
            
            if (queryData.showDiagram === true) {
                // OemManuals flow: diagram + part cards
                this.isDiagramSearch = true;
                this.showDiagram = false; // this will need to be changed back to true when diagram display is approved
                this.searchResults = {};
                this.totalParts = 0;
                this.loadingSearchResults = true;
                this.diagramComponentName = queryData.component || "";
                // Clear the previous component's diagram so a spinner shows while the new one loads
                this.diagramUrl = "";
                this.diagramSvgData = "";
                this.diagramLoading = true;
                // Store the SVG diagram data if provided
                if (queryData.diagramSvgData) {
                    this.diagramSvgData = queryData.diagramSvgData;
                    this.diagramLoading = false;
                }
                this.loadOemManualsDiagram(queryData);
            } else {
                // Category-based parts list via TmsService
                this.isDiagramSearch = false;
                this.showDiagram = false;
                this.loadingSearchResults = true;
                if (this.selectedSortTerm === "") {
                    // Load user default, or if there is none, default to popularity
                    this.selectedSortTerm = this.partSearchDefaultSortValue === "" ? "salesVolumeDesc" : this.partSearchDefaultSortValue
                }
                const sortOption = this.sortByOptions.find(option => option.value === this.selectedSortTerm);

                this.getCategoryParts(sortOption?.sortKey, sortOption?.sortDirection, 1, false);
            }
        },

        /**
         * Loads parts and a diagram image from OemManualsService and
         * enriches each part with FullPartDetails from PartsService.
         * The diagram fetch and part-detail enrichment run concurrently so
         * the (often slower) diagram call never blocks the parts list.
         */
        async loadOemManualsDiagram(queryData: any) {
            try {
                const leafParts: any[] = queryData.inlineParts || [];

                // Transform leaf parts to the shape expected by the results panel
                const transformedParts = leafParts.map((part: any) => ({
                    PartNumber: part.PartNumber,
                    OemPartCode: part.PartNumber,
                    PartCode: part.PartNumber,
                    Description: part.PartDescription,
                    PopularDescription: part.PartDescription,
                    FullHierarchyPath: part.FullHierarchyPath,
                    EffectiveFrom: part.EffectiveFrom,
                    EffectiveTo: part.EffectiveTo,
                    PartId: part.PartId,
                    // Matches the `name` attribute on the diagram SVG's hotspot <g hsid="..."> elements
                    ItemNumber: part.ItemNumber,
                    FullPartDetails: null
                }));

                this.searchResults = {
                    Parts: transformedParts,
                    TotalCount: leafParts.length,
                    OemDescription: queryData.oemDescription,
                    Model: queryData.model
                };

                this.totalParts = this.searchResults.TotalCount;
                this.totalPartPages = Math.ceil(this.totalParts / this.partsPerPage);

                // The parts list is ready to render now — each row shows its own
                // skeleton via `!part.FullPartDetails`, so stop blocking the whole
                // panel behind `loading` while the diagram and part details load.
                this.loadingSearchResults = false;

                await Promise.all([
                    this.loadDiagramImage(queryData),
                    this.enrichPartsWithFullDetails(this.searchResults.Parts)
                ]);
            } catch (error) {
                console.error("Error loading OemManuals diagram:", error);
                ErrorLogService.LogException(error);
                this.loadingSearchResults = false;
                this.diagramLoading = false;
            }
        },

        /** Fetches (or reuses) the SVG/PNG diagram for the current component and stores it on the page. */
        async loadDiagramImage(queryData: any) {
            if (queryData.diagramSvgData) {
                this.diagramSvgData = queryData.diagramSvgData;
                this.diagramAltText = `${queryData.component} diagram`;
                this.diagramLoading = false;
                return;
            }

            if (!FeatureFlagService.getFeatureEnabled(constants.FEATURE_NAMES.SHOW_DIAGRAM_DATA)) {
                this.diagramLoading = false;
                return;
            }

            if (!queryData.componentGroupId) {
                this.diagramLoading = false;
                return;
            }

            try {
                const diagramResponse = await OemManualsService.GetDiagram(
                    queryData.userId,
                    queryData.BrandID,
                    queryData.componentGroupId,
                    OemManualsDiagramFormat.Svg,
                    queryData.artId
                );
                if (!diagramResponse.error) {
                    // Backend DiagramFormat doesn't reliably match the requested format enum,
                    // so detect SVG by decoded content instead of trusting that field.
                    const decoded = OemManualsService.DecodeSvgPayload(diagramResponse.DiagramData);
                    const isSvgMarkup = /^\s*(<\?xml|<svg)/i.test(decoded);
                    if (isSvgMarkup) {
                        // Keep markup as raw SVG (not a data URI) so it can be inlined via
                        // v-html and its elements matched to parts by hsid/name for highlighting.
                        this.diagramSvgData = decoded;
                        this.diagramUrl = "";
                    } else {
                        this.diagramUrl = `data:image/png;base64,${diagramResponse.DiagramData}`;
                        this.diagramSvgData = "";
                    }
                    this.diagramAltText = `${queryData.component} diagram`;
                } else {
                    console.warn("Diagram returned an error:", diagramResponse.error.Message);
                    this.diagramUrl = "";
                    this.diagramSvgData = "";
                    this.diagramAltText = "";
                    this.showDiagram = false;
                }
            } catch (diagramErr) {
                this.diagramUrl = "";
                this.diagramSvgData = "";
                this.diagramAltText = "";
                this.showDiagram = false;
            } finally {
                this.diagramLoading = false;
            }
        },

        /** Fetches FullPartDetails for every part in parallel, enriching each part object in place. */
        async enrichPartsWithFullDetails(currentParts: any[]) {
            if (!currentParts.length) return;

            let detailsFetched = 0;
            await new Promise<void>((resolve) => {
                currentParts.forEach((part: any, index: number) => {
                    const partCode = part.PartNumber || part.OemPartCode;
                    // SmartEquip PartId is a MasterPartId, not a DealerNet Part.Id.
                    // Pass 0 so the API resolves the part by oemPartCode instead.
                    PartsService.FullPartDetails(
                        this.activeAccountId,
                        this.user.UserId,
                        null,
                        this.audienceIds,
                        partCode
                    )
                        .then((fullPartDetail) => {
                            this.$set(currentParts[index], "FullPartDetails", fullPartDetail.data);
                            if (fullPartDetail.data?.PartId) {
                                this.$set(currentParts[index], "PartId", fullPartDetail.data.PartId);
                            }
                        })
                        .catch((err) => {
                            console.warn(`Could not fetch details for part ${partCode}:`, err);
                            // Provide fallback details so the card can still render
                            this.$set(currentParts[index], "FullPartDetails", {
                                OemPartCode: part.OemPartCode,
                                PartDescription: part.Description,
                                Quantity: part.Quantity
                            });
                        })
                        .finally(() => {
                            if (part.FullPartDetails && !part.FullPartDetails.OemPartCode) {
                                part.FullPartDetails.OemPartCode = part.OemPartCode;
                                part.FullPartDetails.PartDescription = part.Description;
                            }

                            if (++detailsFetched === currentParts.length) {
                                resolve();
                            }
                        });
                });
            });
        },


        /** Resolves the default sort and triggers a diagram parts search. */
        performDiagramSearch() {
            if (this.isDiagramSearch) {
                return;
            }

            if (this.selectedSortTerm === "") {
                this.selectedSortTerm = this.partSearchDefaultSortValue === ""
                    ? "salesVolumeDesc"
                    : this.partSearchDefaultSortValue;
            }
            const sortOption = this.sortByOptions.find((o: any) => o.value === this.selectedSortTerm);
            this.getCategoryParts(sortOption?.sortKey, sortOption?.sortDirection, 1, false);
        },
    }
});
